import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { MsalRuntimeConfigService } from '../msgraph/msal-runtime-config.service';
import { SessionService } from '@services/index.service';
import { AngularPaths } from '@models/index.model';
import { TranslateService } from '@ngx-translate/core';
import { Observable, catchError, map, of } from 'rxjs';

/**
 * Guard específico para la entrada /signtrust/:spConfig
 * 1) Si el token está vivo -> permitir (WebSignComponent decide si usa SharePoint).
 * 2) Si el token caducó pero hay refreshToken -> refresco silencioso ANTES de
 *    decidir. Si el refresh falla (sesión muerta), refreshSessionShared limpia la
 *    sesión y aquí guardamos el hash y vamos a Login (se retoma tras autenticar).
 * 3) Si no hay refreshToken -> guardamos el hash y vamos a Login.
 *
 * Es refresh-aware (como AuthGuard) para no rebotar a la home con un token
 * "fantasma" cuando la sesión ya no es válida.
 */
@Injectable({ providedIn: 'root' })
export class SpConfigGuard {
  constructor(
    private readonly cfg: MsalRuntimeConfigService,
    private readonly session: SessionService,
    private readonly router: Router,
    private readonly _translate: TranslateService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | boolean | UrlTree {
    const spConfigId = route.paramMap.get('spConfig') ?? '';

    // 1) Token vivo.
    if (this.session.isLoggedIn() && this.session.checkTokenValid()) {
      this._warnIfNoSpConfig();
      return true;
    }

    // 2) Token caducado con refreshToken -> refresco silencioso y esperamos.
    if (this.session.hasRefreshToken()) {
      return this.session.refreshSessionShared({ source: 'SpConfigGuard' }).pipe(
        map((): boolean | UrlTree => {
          this._warnIfNoSpConfig();
          return true;
        }),
        catchError(() => of(this._toLogin(spConfigId)))
      );
    }

    // 3) Sin sesión utilizable.
    return this._toLogin(spConfigId);
  }

  private _toLogin(spConfigId: string): UrlTree {
    // Guardamos el hash de un solo uso para retomar el flujo tras el login.
    if (spConfigId) localStorage.setItem('spRedirectId', spConfigId);
    return this.router.parseUrl(`/${AngularPaths.LOGIN}`);
  }

  private _warnIfNoSpConfig(): void {
    const c = this.cfg.get();
    const ok = !!c?.sharepoint_clientid && !!c?.sharepoint_tenantid;
    if (!ok) {
      console.warn(this._translate.instant('WEB-SIGN.SIGNTRUST-LIB.ERRORS.NO-SP-CONFIG'));
    }
  }
}
